import { useEffect, useState } from "react";
import { Bell, CalendarCheck, BellRing, Syringe, PackageX, X, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, mensajeError } from "../../api/client";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { useAuth } from "../../context/AuthContext";

interface ItemCita {
  id: string;
  fechaInicio: string;
  tipoServicio?: string;
  mascota?: { nombre?: string } | null;
  cliente?: { nombre?: string } | null;
}
interface ItemRecordatorio {
  id: string;
  programadoPara: string;
  canal: string;
  tipo: string;
  cliente?: { nombre?: string } | null;
  mascota?: { nombre?: string } | null;
}
interface ItemVacuna {
  id: string;
  nombre: string;
  fechaProximaDosis: string;
  mascota?: { nombre?: string } | null;
}
interface ItemProducto {
  id: string;
  nombre: string;
  caducidad: string;
}
interface ItemMensajeCliente {
  id: string;
  nombrePerfil?: string | null;
  telefono: string;
  ultimoMensaje?: string | null;
  ultimoMensajeEn?: string | null;
}
interface NotificacionesResumen {
  citasHoy: ItemCita[];
  recordatoriosEnviados: ItemRecordatorio[];
  recordatoriosPendientes: ItemRecordatorio[];
  vacunasProximas: ItemVacuna[];
  porCaducar: ItemProducto[];
  mensajesClientesSinLeer: ItemMensajeCliente[];
}

function horaLocal(fecha: string) {
  return new Date(fecha).toLocaleTimeString("es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit" });
}
function fechaCorta(fecha: string) {
  return new Date(fecha).toLocaleDateString("es-MX", { timeZone: "America/Mexico_City", day: "numeric", month: "short" });
}

// Campanita de notificaciones del día en la barra superior del negocio:
// citas de hoy, recordatorios (enviados y pendientes), vacunas próximas y
// productos por caducar — todo en un vistazo, sin tener que entrar al
// Dashboard o a cada módulo por separado.
export function NotificacionesHoy({ claro = false }: { claro?: boolean }) {
  const { permisoSeccion } = useAuth();
  const puedeVer = permisoSeccion("dashboard") !== "OCULTO";
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState<NotificacionesResumen | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!puedeVer) return;
    cargar();
    const id = setInterval(cargar, 5 * 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer]);

  function cargar() {
    setCargando(true);
    api
      .get("/reportes/notificaciones-hoy")
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)))
      .finally(() => setCargando(false));
  }

  if (!puedeVer) return null;

  const totalAvisos =
    (datos?.citasHoy.length ?? 0) +
    (datos?.recordatoriosPendientes.length ?? 0) +
    (datos?.vacunasProximas.length ?? 0) +
    (datos?.porCaducar.length ?? 0) +
    (datos?.mensajesClientesSinLeer.length ?? 0);

  return (
    <div className="relative">
      <button
        onClick={() => setAbierto((v) => !v)}
        className={`relative flex h-9 w-9 items-center justify-center rounded-xl ${
          claro ? "text-blue-200/80 hover:bg-white/10 hover:text-white" : "text-slate-500 hover:bg-slate-50"
        }`}
        title="Notificaciones de hoy"
      >
        <Bell size={18} />
        {totalAvisos > 0 && (
          <span className="animar-latido absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {totalAvisos > 99 ? "99+" : totalAvisos}
          </span>
        )}
      </button>

      {abierto && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setAbierto(false)} />
          <div className="absolute right-0 z-20 mt-2 max-h-[28rem] w-80 overflow-y-auto rounded-2xl border border-slate-100 bg-white shadow-lg sm:w-96">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-4 py-3">
              <p className="text-sm font-semibold text-slate-700">Novedades de hoy</p>
              <button onClick={() => setAbierto(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <X size={15} />
              </button>
            </div>

            <div className="p-3">
              {cargando && !datos ? (
                <div className="flex justify-center py-10"><Spinner /></div>
              ) : error ? (
                <p className="p-4 text-xs text-red-500">{error}</p>
              ) : (
                <div className="space-y-4">
                  {datos && datos.mensajesClientesSinLeer.length > 0 && (
                    <Bloque
                      icono={<MessageCircle size={14} className="text-teal-500" />}
                      titulo="Mensajes de clientes sin leer"
                      vacio=""
                    >
                      {datos.mensajesClientesSinLeer.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setAbierto(false);
                            navigate("/respuestas-clientes");
                          }}
                          className="flex w-full items-center justify-between gap-2 rounded-xl bg-teal-50 px-2 py-1.5 text-left text-sm hover:bg-teal-100"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-700">{m.nombrePerfil || m.telefono}</p>
                            <p className="truncate text-xs text-slate-500">{m.ultimoMensaje || "—"}</p>
                          </div>
                          {m.ultimoMensajeEn && <span className="shrink-0 text-xs text-slate-400">{fechaCorta(m.ultimoMensajeEn)}</span>}
                        </button>
                      ))}
                    </Bloque>
                  )}

                  <Bloque
                    icono={<CalendarCheck size={14} className="text-sky-500" />}
                    titulo="Citas de hoy"
                    vacio="No hay citas agendadas para hoy."
                  >
                    {datos?.citasHoy.map((c) => (
                      <Fila
                        key={c.id}
                        principal={`${c.mascota?.nombre ?? "—"} · ${c.cliente?.nombre ?? ""}`}
                        secundario={c.tipoServicio}
                        derecha={horaLocal(c.fechaInicio)}
                      />
                    ))}
                  </Bloque>

                  <Bloque
                    icono={<BellRing size={14} className="text-amber-500" />}
                    titulo="Recordatorios de hoy"
                    vacio="No hay recordatorios programados para hoy."
                  >
                    {datos?.recordatoriosPendientes.map((r) => (
                      <Fila
                        key={r.id}
                        principal={`${r.cliente?.nombre ?? ""}${r.mascota?.nombre ? ` · ${r.mascota.nombre}` : ""}`}
                        secundario={`Por enviar · ${r.canal.toLowerCase()}`}
                        derecha={horaLocal(r.programadoPara)}
                      />
                    ))}
                    {datos?.recordatoriosEnviados.map((r) => (
                      <Fila
                        key={r.id}
                        principal={`${r.cliente?.nombre ?? ""}${r.mascota?.nombre ? ` · ${r.mascota.nombre}` : ""}`}
                        secundario={`Ya enviado · ${r.canal.toLowerCase()}`}
                        derecha={horaLocal(r.programadoPara)}
                        atenuado
                      />
                    ))}
                  </Bloque>

                  <Bloque
                    icono={<Syringe size={14} className="text-violet-500" />}
                    titulo="Vacunas próximas (7 días)"
                    vacio="Sin vacunas próximas a vencer."
                  >
                    {datos?.vacunasProximas.map((v) => (
                      <Fila key={v.id} principal={`${v.mascota?.nombre ?? "—"} · ${v.nombre}`} derecha={fechaCorta(v.fechaProximaDosis)} />
                    ))}
                  </Bloque>

                  <Bloque
                    icono={<PackageX size={14} className="text-red-500" />}
                    titulo="Productos por caducar (30 días)"
                    vacio="Sin productos próximos a caducar."
                  >
                    {datos?.porCaducar.map((p) => (
                      <Fila key={p.id} principal={p.nombre} derecha={fechaCorta(p.caducidad)} />
                    ))}
                  </Bloque>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Bloque({ icono, titulo, vacio, children }: { icono: React.ReactNode; titulo: string; vacio: string; children: React.ReactNode }) {
  const hayContenido = ([] as React.ReactNode[]).concat(children as any).flat(Infinity).filter(Boolean).length > 0;
  return (
    <div>
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
        {icono} {titulo}
      </p>
      {hayContenido ? <ul className="space-y-1.5">{children}</ul> : <EmptyState compacto titulo={vacio} />}
    </div>
  );
}

function Fila({ principal, secundario, derecha, atenuado }: { principal: string; secundario?: string; derecha: string; atenuado?: boolean }) {
  return (
    <li className={`flex items-center justify-between gap-2 rounded-xl px-2 py-1.5 text-sm ${atenuado ? "opacity-60" : "bg-slate-50"}`}>
      <div className="min-w-0">
        <p className="truncate font-medium text-slate-700">{principal}</p>
        {secundario && <p className="truncate text-xs text-slate-400">{secundario}</p>}
      </div>
      <span className="shrink-0 text-xs text-slate-400">{derecha}</span>
    </li>
  );
}
